import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import type { CountyFeatureCollection } from './usePowerOutageCounties';

export type EriCountyProperties = {
  boundaryId: string;
  stateName: string | null;
  countyName: string | null;
  eriMean: number | null;
  eri10: number | null;
  eri25: number | null;
  eri75: number | null;
  eri90: number | null;
  maxWind: number | null;
  awetsnMean: number | null;
  framThickMean: number | null;
  thunderstorm: number | null;
  /** Forecast step this row describes (ISO). */
  timestamp: string | null;
};

export type EriCountiesResponse = {
  /** ISO timestamp of the model run, null when no ERI data exists. */
  modelRuntime: string | null;
  /** Hours since modelRuntime, null when there is no run. */
  modelAgeHours: number | null;
  /** True when the run is older than the server freshness threshold. */
  stale: boolean;
  /** Ascending ISO forecast steps available for this run. */
  steps: string[];
  /**
   * The step the features describe (requested step when valid, else next
   * upcoming, else last).
   */
  selectedStep: string | null;
  featureCollection: CountyFeatureCollection<EriCountyProperties>;
};

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const eriCountiesQueryKey = (step: string | null) =>
  ['map', 'eriCounties', step ?? 'auto'] as const;

/**
 * Latest ERI model run for one forecast step; only fetched while the layer is
 * shown.
 */
export const useEriCounties = (enabled: boolean, step: string | null) => {
  return useQuery({
    queryKey: eriCountiesQueryKey(step),
    enabled,
    staleTime: FIVE_MINUTES_MS,
    queryFn: async () => {
      const response = await axios.get<EriCountiesResponse>(
        config.apiGateway.eriCounties,
        {
          params: step ? { step } : undefined,
        }
      );
      return response.data;
    },
  });
};
